"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

// sessionStorage is scoped per browser tab and survives in-tab navigation +
// refreshes, but resets when the tab closes. That matches "first page in this
// tab" exactly.
const KEY = "nf:tab-splash-shown";
// React Strict Mode (dev) mounts the component twice on first load with a
// cleanup between. The first effect run writes sessionStorage; the second
// read would otherwise look like a repeat visit. Anything written within
// this window is treated as our own write and ignored.
const SELF_WRITE_WINDOW_MS = 2000;

// Splash choreography (when userName is known):
//   waiting   → NF visible, welcome slot empty (space reserved)
//   welcome   → welcome message fades in
//   +400ms    → NF expands to NewForm (ew/orm slide out)
//   +EXPAND   → settled, full NewForm + welcome visible
//   +400ms    → exiting (whole splash fades out)
const WELCOME_FADE_MS = 500;
const WELCOME_TO_EXPAND_MS = 400;
const EXPAND_DURATION_MS = 750; // matches CSS: 0.75s duration, no stagger
const SETTLED_HOLD_MS = 400;
// Fallback: if the user name never resolves (unauthenticated, slow auth, etc.)
// we still want the splash to come and go in a reasonable time.
const NO_NAME_FALLBACK_MS = 1800;

type SplashState =
  | "waiting" // NF visible, welcome hidden, awaiting name
  | "welcome" // welcome fading in
  | "expanding" // NF -> NewForm in progress
  | "settled" // full NewForm + welcome
  | "exiting" // splash fading out
  | "hidden"; // unmounted

const WELCOME_VARIANTS: ((name: string) => string)[] = [
  (n) => `Welcome back, ${n}.`,
  (n) => `Hey ${n}, your batches missed you.`,
  (n) => `${n}, the engine never stopped.`,
  (n) => `Good to have you back, ${n}.`,
  (n) => `${n}, let's see what's winning today.`,
  (n) => `Picking up where you left off, ${n}.`,
  (n) => `${n}, your dashboard is ready.`,
  (n) => `Hello again, ${n}.`,
  (n) => `${n}, time to ship some creative.`,
  (n) => `Ready when you are, ${n}.`,
];

type Props = {
  userName?: string | null;
};

const GRID_CELL_PX = 60;
const GRID_STROKE_OPACITY = 0.04;
const GRID_LINE_DRAW_MS = 950;
// Maximum delay applied to the lines furthest from the screen center.
// Lines at the center start at 0ms; the edge-most lines wait this long.
const GRID_MAX_STAGGER_MS = 850;

type GridLine = {
  key: string;
  orientation: "horizontal" | "vertical";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  delayMs: number;
};

function GridPaint({ painted }: { painted: boolean }) {
  // viewport is null on SSR + first client render so we can't render any
  // lines yet — the splash overlay is visible (cream background) but the
  // grid waits until we know the canvas size. The grid only paints when
  // `painted` flips true (post-"waiting"), so this short delay is invisible.
  const [vp, setVp] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    setVp({ w: window.innerWidth, h: window.innerHeight });
  }, []);

  // Defer the painted prop by one animation frame. Without this, the parent
  // state machine often resolves vp + transitions from "waiting" → "welcome"
  // in the same React commit, so the lines first paint at scale(1,1) and
  // the CSS transition has nothing to animate from. Forcing a frame at the
  // initial `false` state lets the browser register the "from" snapshot.
  const [appliedPainted, setAppliedPainted] = useState(false);
  useEffect(() => {
    if (!vp) return;
    if (!painted) {
      setAppliedPainted(false);
      return;
    }
    const raf = requestAnimationFrame(() => {
      // Two RAFs deep — Safari sometimes coalesces a single RAF with the
      // commit that set vp, defeating the deferral.
      requestAnimationFrame(() => setAppliedPainted(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [painted, vp]);

  const lines = useMemo<GridLine[]>(() => {
    if (!vp) return [];
    const centerX = vp.w / 2;
    const centerY = vp.h / 2;
    // Normalize per-orientation: the central horizontal line and the
    // central vertical line both start at delay 0; the outermost lines
    // of each orientation hit GRID_MAX_STAGGER_MS. The whole grid then
    // appears to bloom symmetrically from the screen center.
    const maxYDist = vp.h / 2;
    const maxXDist = vp.w / 2;
    const horizontals = Math.floor(vp.h / GRID_CELL_PX);
    const verticals = Math.floor(vp.w / GRID_CELL_PX);
    const out: GridLine[] = [];
    for (let i = 1; i <= horizontals; i++) {
      const y = i * GRID_CELL_PX;
      const distance = Math.abs(y - centerY);
      out.push({
        key: `h${i}`,
        orientation: "horizontal",
        x1: 0,
        y1: y,
        x2: vp.w,
        y2: y,
        delayMs: maxYDist ? (distance / maxYDist) * GRID_MAX_STAGGER_MS : 0,
      });
    }
    for (let i = 1; i <= verticals; i++) {
      const x = i * GRID_CELL_PX;
      const distance = Math.abs(x - centerX);
      out.push({
        key: `v${i}`,
        orientation: "vertical",
        x1: x,
        y1: 0,
        x2: x,
        y2: vp.h,
        delayMs: maxXDist ? (distance / maxXDist) * GRID_MAX_STAGGER_MS : 0,
      });
    }
    return out;
  }, [vp]);

  if (!vp) return null;

  return (
    <svg
      aria-hidden="true"
      width={vp.w}
      height={vp.h}
      viewBox={`0 0 ${vp.w} ${vp.h}`}
      className="absolute inset-0 pointer-events-none"
    >
      <title>NewForm grid backdrop</title>
      {lines.map((line) => (
        <line
          key={line.key}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke="#000"
          strokeWidth={1}
          strokeOpacity={GRID_STROKE_OPACITY}
          style={{
            // fill-box pins transform-origin to the line's own bounding box
            // (its midpoint) instead of the SVG canvas origin (0,0). Without
            // this, scaleX/scaleY would collapse toward 0,0 and the lines
            // would visibly slide as they grow.
            transformBox: "fill-box",
            transformOrigin: "center",
            // Same transform-function signature in both states so the
            // browser interpolates smoothly. Mixing `scaleX(0)` ↔ `scale(1,1)`
            // makes the browser treat them as different functions and snap
            // to the end value instead of animating.
            transform: appliedPainted
              ? "scale(1, 1)"
              : line.orientation === "horizontal"
                ? "scale(0, 1)"
                : "scale(1, 0)",
            // easeInOutCubic — symmetric S-curve, gentle at both ends.
            // Reads more "fluid" than the front-loaded easeOutQuint we had
            // before, especially for a stretching/growing motion.
            transition: `transform ${GRID_LINE_DRAW_MS}ms cubic-bezier(0.65, 0, 0.35, 1) ${line.delayMs}ms`,
            willChange: "transform",
          }}
        />
      ))}
    </svg>
  );
}

export function FirstVisitSplash({ userName }: Props) {
  const [state, setState] = useState<SplashState>("waiting");
  // Mounted gate: ensures Math.random() only fires client-side after
  // hydration. Otherwise SSR picks one variant and the client picks
  // another, producing a text-content hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Measured natural widths of "ew" / "orm" in the Swear font. Animating
  // max-width to a *guessed* em value caused the visible width to clip at
  // the natural cap partway through the animation, producing the "stops
  // abruptly" feel. Measuring lets us drive max-width to the exact final
  // value so the animation curve maps 1:1 to visible motion.
  const measureEwRef = useRef<HTMLSpanElement>(null);
  const measureOrmRef = useRef<HTMLSpanElement>(null);
  const [extraTargets, setExtraTargets] = useState<{
    ew: string;
    orm: string;
  }>({
    // Generous fallbacks used on the very first render before measurement.
    // The actual animation only runs after state transitions to "expanding",
    // by which point the measurement effect has long since updated these.
    ew: "1.4em",
    orm: "2.05em",
  });

  useEffect(() => {
    const measure = () => {
      const ewEl = measureEwRef.current;
      const ormEl = measureOrmRef.current;
      if (!ewEl || !ormEl) return;
      const ew = ewEl.offsetWidth;
      const orm = ormEl.offsetWidth;
      if (ew > 0 && orm > 0) {
        setExtraTargets({ ew: `${ew}px`, orm: `${orm}px` });
      }
    };
    measure();
    // Swear is a local font with display:block, so glyph metrics aren't
    // final until the font is loaded. Re-measure once fonts ready.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
  }, []);

  // Stable random pick — chosen once per session after mount. The welcome
  // span has opacity: 0 during the "waiting" phase, so the post-hydration
  // content swap is invisible to the user.
  const welcomeMessage = useMemo(() => {
    if (!mounted || !userName) return null;
    const pick =
      WELCOME_VARIANTS[Math.floor(Math.random() * WELCOME_VARIANTS.length)];
    return pick(userName);
  }, [mounted, userName]);

  // First effect: decide whether this is a fresh tab visit. If not, drop
  // the splash entirely. Runs once on mount; Strict Mode's second run is
  // ignored via the recent-write timestamp window.
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(KEY);
      if (stored) {
        const writtenAt = Number(stored);
        const isOurRecentWrite =
          Number.isFinite(writtenAt) &&
          Date.now() - writtenAt < SELF_WRITE_WINDOW_MS;
        if (!isOurRecentWrite) {
          setState("hidden");
          return;
        }
      } else {
        sessionStorage.setItem(KEY, String(Date.now()));
      }
    } catch {
      // sessionStorage blocked (Safari private mode). Show splash anyway.
    }
  }, []);

  // Phase machine driven by state-scoped effects. Each effect schedules
  // ONLY the timer for its own state; the cleanup on state change clears
  // a timer that already fired (no-op). This pattern survives Strict
  // Mode's double-mount cleanly — the rescheduled timer fires normally.
  useEffect(() => {
    if (state !== "waiting") return;
    if (userName) {
      setState("welcome");
      return;
    }
    const t = setTimeout(() => setState("expanding"), NO_NAME_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [state, userName]);

  useEffect(() => {
    if (state !== "welcome") return;
    const t = setTimeout(() => setState("expanding"), WELCOME_TO_EXPAND_MS);
    return () => clearTimeout(t);
  }, [state]);

  useEffect(() => {
    if (state !== "expanding") return;
    const t = setTimeout(() => setState("settled"), EXPAND_DURATION_MS);
    return () => clearTimeout(t);
  }, [state]);

  useEffect(() => {
    if (state !== "settled") return;
    const t = setTimeout(() => setState("exiting"), SETTLED_HOLD_MS);
    return () => clearTimeout(t);
  }, [state]);

  if (state === "hidden") return null;

  const showWordmarkExpand = state === "expanding" || state === "settled";
  const showWelcome =
    welcomeMessage != null &&
    (state === "welcome" ||
      state === "expanding" ||
      state === "settled" ||
      state === "exiting");

  return (
    <AnimatePresence onExitComplete={() => setState("hidden")}>
      {state !== "exiting" && (
        <motion.div
          key="nf-tab-splash"
          initial={false}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.65, ease: [0.4, 0, 0.2, 1] }}
          className="nf-tab-splash fixed inset-0 z-[9999] bg-nf-ds-cream flex flex-col items-center justify-center pointer-events-none gap-nf-ds-4"
        >
          <GridPaint painted={state !== "waiting"} />

          <span
            className={`nf-tab-splash-wordmark font-swear text-[clamp(28px,4.5vw,70px)] text-nf-ds-black tracking-[-0.02em] leading-none${
              showWordmarkExpand ? " nf-tab-splash-wordmark--expand" : ""
            }`}
          >
            {"N"}
            <span
              className="nf-tab-splash-extra"
              style={
                {
                  "--nf-extra-delay": "0s",
                  "--nf-extra-target": extraTargets.ew,
                } as React.CSSProperties
              }
            >
              ew
            </span>
            {"F"}
            <span
              className="nf-tab-splash-extra"
              style={
                {
                  "--nf-extra-delay": "0s",
                  "--nf-extra-target": extraTargets.orm,
                } as React.CSSProperties
              }
            >
              orm
            </span>
          </span>

          {/* Off-screen measurement clones — same font/size/tracking as the
              animated spans so offsetWidth equals the natural rendered width
              of "ew" / "orm" in Swear. Position absolute so they don't
              affect layout. */}
          <span
            aria-hidden="true"
            ref={measureEwRef}
            className="absolute opacity-0 pointer-events-none font-swear text-[clamp(28px,4.5vw,70px)] tracking-[-0.02em] leading-none whitespace-pre"
            style={{ left: -9999, top: -9999 }}
          >
            ew
          </span>
          <span
            aria-hidden="true"
            ref={measureOrmRef}
            className="absolute opacity-0 pointer-events-none font-swear text-[clamp(28px,4.5vw,70px)] tracking-[-0.02em] leading-none whitespace-pre"
            style={{ left: -9999, top: -9999 }}
          >
            orm
          </span>

          {/* Slot is always rendered so the wordmark sits at the same
              vertical position whether or not a name has arrived. Content
              is a non-breaking space when there's no message, so the line
              reserves its own height via the font metrics. */}
          <span
            className="nf-tab-splash-welcome font-nf-ds-sans text-[clamp(13px,1.2vw,17px)] text-nf-ds-gray-600 transition-opacity ease-out"
            style={{
              opacity: showWelcome ? 1 : 0,
              transitionDuration: `${WELCOME_FADE_MS}ms`,
            }}
          >
            {welcomeMessage ?? " "}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
